import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Edit } from 'lucide-react';
import toast from 'react-hot-toast';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { ScannerStatusBadge } from '@/components/shared/ScannerStatusBadge';
import { ConnectionStatusBadge } from '@/components/shared/ConnectionStatusBadge';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { useAuth } from '@/hooks/useAuth';
import type { Scanner, Branch, Shelf, Tenant, User } from '@packages/shared-types';
import { getScannerById } from '@/services/scannerService';
import { getTenantById } from '@/services/tenantService';
import { listBranches } from '@/services/branchService';
import { listShelves } from '@/services/shelfService';
import { listUsers } from '@/services/userService';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';

export function ScannerDetailsPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeTenant } = useTenantSwitcher();
  const isRoot = user?.role === 'root';

  const title = useIndustryTranslation('pages.scanners.details.title');
  const description = useIndustryTranslation('pages.scanners.details.description');
  const loadingLabel = useIndustryTranslation('pages.scanners.list.loading');
  const errorLoad = useIndustryTranslation('pages.scanners.error.load');
  const errorNotFound = useIndustryTranslation('pages.scanners.error.notFound');
  const branchLabel = useIndustryTranslation('pages.scanners.form.branch');
  const shelfLabel = useIndustryTranslation('pages.scanners.form.shelf');
  const tenantLabel = useIndustryTranslation('pages.scanners.form.tenant');
  const noOrganizationLabel = useIndustryTranslation(
    'pages.scanners.details.noOrganization',
  );

  const [scanner, setScanner] = useState<Scanner | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadScanner = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    setTenant(null);
    try {
      // Load scanner first so we have tenantId for support users (root without activeTenant)
      const scannerData = await getScannerById(id);
      setScanner(scannerData);
      const scannerTenantId = (scannerData as { tenantId?: string | null }).tenantId;
      const effectiveTenantId = activeTenant?.id ?? scannerTenantId ?? undefined;

      const [branchesData, shelvesData, usersData] = await Promise.all([
        listBranches(effectiveTenantId),
        listShelves(effectiveTenantId),
        listUsers(effectiveTenantId),
      ]);

      setBranches(branchesData);
      setShelves(shelvesData);
      setUsers(usersData);

      if (scannerTenantId) {
        try {
          const tenantData = await getTenantById(scannerTenantId);
          setTenant(tenantData);
        } catch {
          setTenant(null);
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : errorLoad;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [id, errorLoad, activeTenant?.id]);

  useEffect(() => {
    void loadScanner();
  }, [loadScanner]);

  const handleEdit = (): void => {
    if (!scanner) return;
    navigate(`/scanners/${scanner.id}/edit`);
  };

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '—';
    return new Date(date).toLocaleString();
  };

  const formatBatteryLevel = (level: number | null | undefined): string => {
    if (level === null || level === undefined) return '—';
    return `${level}%`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={title}
          description={description}
          onBack={() => navigate(-1)}
        />
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-6">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{loadingLabel}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !scanner) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={title}
          description={description}
          onBack={() => navigate(-1)}
        />
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">
              {error || errorNotFound}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const branch = branches.find((b) => b.id === scanner.branchId);
  const shelf = shelves.find((s) => s.id === scanner.shelfId);
  const assignedUser = users.find((u) => u.id === scanner.assignedUserId);

  // Determine which fields to show based on scanner type
  const isHandheld = scanner.type === 'handheld';
  const isFixed = scanner.type === 'fixed';
  const isStandalone = scanner.type === 'standalone';

  // Handheld scanners: show battery, lastScanAt, and all device info
  // Fixed scanners (antenna): no battery, may not have lastScanAt, but show connection/antenna status
  // Standalone: no Status & Health section; Location shows only organization (limited)
  const shouldShowBattery = isHandheld;
  const shouldShowLastScan = isHandheld || (isFixed && scanner.lastScanAt != null);

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={scanner.name}
        description={description}
        onBack={() => navigate(-1)}
        actions={
          isRoot && !activeTenant ? (
            <Button variant="outline" onClick={handleEdit}>
              <Edit className="mr-2 h-4 w-4" />
              {t('pages.scanners.actions.edit')}
            </Button>
          ) : null
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold">
              {t('pages.scanners.details.sections.basicInfo')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('pages.scanners.form.name')}
                </label>
                <p className="mt-1 text-sm">{scanner.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('pages.scanners.form.deviceId')}
                </label>
                <p className="mt-1 text-sm font-mono">{scanner.deviceId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('pages.scanners.form.type')}
                </label>
                <p className="mt-1 text-sm">
                  {t(`pages.scanners.types.${scanner.type}`)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('pages.scanners.form.status')}
                </label>
                <div className="mt-1">
                  <ScannerStatusBadge status={scanner.status} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold">
              {t('pages.scanners.details.sections.location')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {tenantLabel}
                </label>
                <div className="mt-1 flex items-center gap-2">
                  {tenant ? (
                    <>
                      <Avatar className="h-8 w-8 border border-border">
                        {tenant.profileImage ? (
                          <AvatarImage
                            src={tenant.profileImage}
                            alt={tenant.name}
                          />
                        ) : null}
                        <AvatarFallback className="bg-muted text-muted-foreground">
                          <Building2 className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{tenant.name}</span>
                    </>
                  ) : (
                    <span className="text-sm">{noOrganizationLabel}</span>
                  )}
                </div>
              </div>
              {!isStandalone && (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {branchLabel}
                    </label>
                    <p className="mt-1 text-sm">{branch?.name || '—'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {shelfLabel}
                    </label>
                    <p className="mt-1 text-sm">{shelf?.name || '—'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('pages.scanners.form.assignedUser')}
                    </label>
                    <p className="mt-1 text-sm">
                      {assignedUser
                        ? `${assignedUser.firstName} ${assignedUser.lastName} (${assignedUser.email})`
                        : '—'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {!isStandalone && (
        <Card>
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold">
              {t('pages.scanners.details.sections.status')}
            </h2>
            <div className="space-y-4">
              {shouldShowBattery && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('pages.scanners.table.batteryLevel')}
                  </label>
                  <p className="mt-1 text-sm">{formatBatteryLevel(scanner.batteryLevel)}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('pages.scanners.table.lastCheckin')}
                </label>
                <p className="mt-1 text-sm">
                  {formatDate(scanner.lastCheckinAt)}
                </p>
              </div>
              {shouldShowLastScan && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('pages.scanners.details.lastScanAt')}
                  </label>
                  <p className="mt-1 text-sm">
                    {formatDate(scanner.lastScanAt)}
                  </p>
                </div>
              )}
              {scanner.firmwareVersion && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('pages.scanners.details.firmwareVersion')}
                  </label>
                  <p className="mt-1 text-sm font-mono">
                    {scanner.firmwareVersion}
                  </p>
                </div>
              )}
              {scanner.temperature != null && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('pages.scanners.details.temperature')}
                  </label>
                  <p className="mt-1 text-sm">{scanner.temperature}°C</p>
                </div>
              )}
              {scanner.powerLevel != null && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('pages.scanners.details.powerLevel')}
                  </label>
                  <p className="mt-1 text-sm">{scanner.powerLevel} dBm</p>
                </div>
              )}
              {scanner.antennaStatus && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('pages.scanners.details.antennaStatus')}
                  </label>
                  <div className="mt-1">
                    <ConnectionStatusBadge status={scanner.antennaStatus} />
                  </div>
                </div>
              )}
              {scanner.connectionStatus && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('pages.scanners.details.connectionStatus')}
                  </label>
                  <div className="mt-1">
                    <ConnectionStatusBadge status={scanner.connectionStatus} />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        <Card>
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold">
              {t('pages.scanners.details.sections.metadata')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('pages.scanners.details.createdAt')}
                </label>
                <p className="mt-1 text-sm">{formatDate(scanner.createdAt)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('pages.scanners.details.updatedAt')}
                </label>
                <p className="mt-1 text-sm">{formatDate(scanner.updatedAt)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
